import { IUser } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../../../web/components-v2/Button';
import { ComboboxContent, ComboboxInput, ComboboxList } from '../../../../../../web/components-v2/Combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../../web/components/Select';
import {
	UserCombobox,
	UserComboboxAvatarItem,
	UserComboboxStatus,
} from '../../../../../../web/components/UserCombobox';
import { bs } from '../../../../../../web/styles/styles';
import type { IPracticeCallFilter } from '../../hooks/usePracticeCallsSearch';
import { styleSheet } from '../CallFilters/styles';

interface PracticeCallFiltersProps {
	filter: IPracticeCallFilter;
	onFilterChange: (filter: IPracticeCallFilter) => void;
	onClearFilters: () => void;
}

// Date range presets
const dateRangePresets = [
	{ label: 'Today', value: 'today' },
	{ label: 'Yesterday', value: 'yesterday' },
	{ label: 'Last 7 Days', value: 'last7days' },
	{ label: 'Last 30 Days', value: 'last30days' },
	{ label: 'This Month', value: 'thismonth' },
	{ label: 'Last Month', value: 'lastmonth' },
	{ label: 'Custom', value: 'custom' },
];

export const PracticeCallFilters: React.FC<PracticeCallFiltersProps> = ({ filter, onFilterChange, onClearFilters }) => {
	const id = React.useId();

	const handleUserChange = React.useCallback(
		(user: IUser | null) => {
			if (!user) {
				const newFilter = { ...filter };
				delete newFilter.user;
				onFilterChange(newFilter);
				return;
			}
			onFilterChange({ ...filter, user });
		},
		[filter, onFilterChange]
	);

	const handleDateRangePresetChange = React.useCallback(
		(preset: string) => {
			if (preset === 'custom') return;

			const now = new Date();
			const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
			let startDate: Date;
			let endDate: Date = new Date(todayDate.getTime() + 24 * 60 * 60 * 1000 - 1);

			switch (preset) {
				case 'today':
					startDate = todayDate;
					break;
				case 'yesterday':
					startDate = new Date(todayDate.getTime() - 24 * 60 * 60 * 1000);
					endDate = new Date(todayDate.getTime() - 1);
					break;
				case 'last7days':
					startDate = new Date(todayDate.getTime() - 7 * 24 * 60 * 60 * 1000);
					endDate = new Date(todayDate.getTime() + 24 * 60 * 60 * 1000 - 1);
					break;
				case 'last30days':
					startDate = new Date(todayDate.getTime() - 30 * 24 * 60 * 60 * 1000);
					endDate = new Date(todayDate.getTime() + 24 * 60 * 60 * 1000 - 1);
					break;
				case 'thismonth':
					startDate = new Date(now.getFullYear(), now.getMonth(), 1);
					endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
					break;
				case 'lastmonth':
					startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
					endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
					break;
				default:
					return;
			}

			onFilterChange({
				...filter,
				date: { start: startDate, end: endDate },
			});
		},
		[filter, onFilterChange]
	);

	const handleDateRangeChange = React.useCallback(
		(field: 'start' | 'end', date: string) => {
			const parsed = new Date(date);
			if (isNaN(parsed.getTime())) return;

			const currentDate = filter.date || { start: new Date(), end: new Date() };
			const newDate = {
				...currentDate,
				[field]: parsed,
			};
			onFilterChange({
				...filter,
				date: newDate,
			});
		},
		[filter, onFilterChange]
	);

	const getCurrentPreset = React.useCallback(() => {
		if (!filter.date) return 'last30days';

		const { start, end } = filter.date;
		const now = new Date();
		const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
		const yesterday = new Date(todayDate.getTime() - 24 * 60 * 60 * 1000);

		if (start.getTime() >= todayDate.getTime() && end.getTime() <= todayDate.getTime() + 24 * 60 * 60 * 1000) {
			return 'today';
		}

		if (start.getTime() >= yesterday.getTime() && end.getTime() <= todayDate.getTime()) {
			return 'yesterday';
		}

		const sevenDaysAgo = new Date(todayDate.getTime() - 7 * 24 * 60 * 60 * 1000);
		if (
			Math.abs(start.getTime() - sevenDaysAgo.getTime()) < 24 * 60 * 60 * 1000 &&
			end.getTime() > todayDate.getTime()
		) {
			return 'last7days';
		}

		const thirtyDaysAgo = new Date(todayDate.getTime() - 30 * 24 * 60 * 60 * 1000);
		if (
			Math.abs(start.getTime() - thirtyDaysAgo.getTime()) < 24 * 60 * 60 * 1000 &&
			end.getTime() > todayDate.getTime()
		) {
			return 'last30days';
		}

		return 'custom';
	}, [filter.date]);

	const today = new Date().toISOString().split('T')[0];
	const formatDateValue = (date: Date | undefined) => {
		if (!date || isNaN(date.getTime())) return today;
		return date.toISOString().split('T')[0];
	};

	return (
		<div className={css(bs.flex, bs.flexCol, bs.gap2)}>
			<div className={css(styleSheet.clearFilterGroup)}>
				<Button variant='secondary' size='sm' onClick={onClearFilters}>
					Clear All Filters
				</Button>
			</div>
			<div className={`${css(styleSheet.container)} practiceCallFilters`}>
				{/* Date Range Helper */}
				<div className={css(styleSheet.filterGroup)}>
					<label className={css(styleSheet.label)} htmlFor='quick-select'>
						Quick Select
					</label>
					<Select items={dateRangePresets} value={getCurrentPreset()} onValueChange={handleDateRangePresetChange}>
						<SelectTrigger id='quick-select' styles={[bs.wFull]}>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{dateRangePresets.map(preset => (
								<SelectItem key={preset.value} value={preset.value}>
									{preset.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				{/* Date Range */}
				<div className={css(styleSheet.filterGroup)}>
					<label className={css(styleSheet.label)}>Date Range</label>
					<div className={css(styleSheet.dateRange)}>
						<input
							type='date'
							value={formatDateValue(filter.date?.start)}
							onChange={e => handleDateRangeChange('start', e.target.value)}
							className={css(styleSheet.dateInput)}
						/>
						<span className={css(styleSheet.dateSeparator)}>to</span>
						<input
							type='date'
							value={formatDateValue(filter.date?.end)}
							onChange={e => handleDateRangeChange('end', e.target.value)}
							className={css(styleSheet.dateInput)}
						/>
					</div>
				</div>

				{/* User Filter */}
				<UserCombobox id={filter.user?.id} key={filter.user?.id} onValueChange={handleUserChange}>
					<div className={css(bs.flex, bs.flexCol, bs.gap1)}>
						<label htmlFor={`${id}-user-combobox`} className={css(bs.textXs, bs.fontBold)}>
							SDR
						</label>
						<ComboboxInput placeholder='Filter by SDR' id={`${id}-user-combobox`} showClear />
					</div>
					<ComboboxContent>
						<UserComboboxStatus />
						<ComboboxList>{(user: IUser) => <UserComboboxAvatarItem key={user.id} user={user} />}</ComboboxList>
					</ComboboxContent>
				</UserCombobox>
			</div>
		</div>
	);
};
